/**
 * URLからのフルページキャプチャ (スマホ版・PC版) の API と結果の読み方 (docs/features/swipe-motion-clip-plan.md §11.2)。
 * `POST /api/jobs/url-capture` で非同期ジョブを投入し、完了の result_data に端末別の成否が入る (11.2.4)。
 * 保存先は個人フォルダ (temporary)・取得元 `source_type='url_capture'` (表示「スクリーンショット」)。
 */
import type { AxiosError } from 'axios';

import { apiClient } from '../../services/apiClient';
import type { CreateJobResponse } from '../../services/asyncJobApi';

export type UrlCaptureDevice = 'phone' | 'pc';

/** 撮る順 = 表示順 = モーダルの初期選択の優先順 (スマホ版があればスマホ版・11.2.8)。 */
export const URL_CAPTURE_DEVICES: readonly UrlCaptureDevice[] = ['phone', 'pc'];

export const URL_CAPTURE_DEVICE_LABELS: Record<UrlCaptureDevice, string> = {
  phone: 'スマホ',
  pc: 'PC',
};

export interface UrlCaptureJobBody {
  url: string;
  devices: UrlCaptureDevice[];
  options?: { keepFixedHeader?: boolean };
}

export type UrlCaptureDeviceErrorCode =
  | 'ACCESS_RESTRICTED'
  | 'NAVIGATION_FAILED'
  | 'TIMEOUT'
  | 'CAPTURE_FAILED'
  | 'STORAGE_QUOTA_EXCEEDED';

export interface UrlCaptureDeviceResult {
  status: 'saved' | 'failed' | 'pending';
  material_id?: string;
  folder?: 'temporary' | 'materials';
  error_code?: UrlCaptureDeviceErrorCode | string;
  error?: string;
  /** 高さの予算 (32000px) で切ったか。BE が台帳に載せていれば完了トーストで注記する (無ければ素材の generation_metadata から読む)。 */
  truncated?: boolean;
}

/** ジョブの result_data (11.2.4)。全端末 failed のときはジョブ自体が failed になる。 */
export interface UrlCaptureJobResult {
  host: string;
  partial: boolean;
  devices: Partial<Record<UrlCaptureDevice, UrlCaptureDeviceResult>>;
}

export async function createUrlCaptureJob(body: UrlCaptureJobBody): Promise<CreateJobResponse> {
  const response = await apiClient.post<CreateJobResponse>('/jobs/url-capture', body);
  return response.data;
}

// ============================================================
// 文言
// ============================================================

/** 端末ごとの失敗理由 (11.2.5-0 / 11.2.8)。 */
export const URL_CAPTURE_DEVICE_ERROR_MESSAGES: Record<UrlCaptureDeviceErrorCode, string> = {
  ACCESS_RESTRICTED: 'このページは会員向け・ログインが必要なため取得できません。お手元でスクリーンショットを撮影して画像としてアップロードしてください',
  NAVIGATION_FAILED: 'ページを開けませんでした。URLをご確認ください',
  TIMEOUT: 'ページの読み込みに時間がかかりすぎたため取得できませんでした',
  CAPTURE_FAILED: 'ページを取得できませんでした。時間をおいてもう一度お試しください',
  STORAGE_QUOTA_EXCEEDED: 'ストレージの容量が不足しているため保存できませんでした',
};

/** 投入時のエラー (11.2.4: 400 / 429 / 402)。BE の error 文言が無いときの既定。 */
export const URL_CAPTURE_REQUEST_ERROR_MESSAGES: Record<string, string> = {
  INVALID_URL: 'このURLは取得できません。公開されているWebページのURLを指定してください',
  CONCURRENT_LIMIT: '実行中のキャプチャがあります。完了してからもう一度お試しください',
  DAILY_LIMIT: '本日のキャプチャの上限に達しました。明日以降にお試しください',
  STORAGE_QUOTA_EXCEEDED: 'ストレージの容量が不足しています。不要な素材を削除してからお試しください',
};

/** 統合ダイアログのフッターのボタンの説明 (ツールチップ)。タッチ端末では出ないので、保存先の1行は本文にも残す */
export const URL_CAPTURE_SCREENSHOT_TOOLTIP = 'ページを上から下までキャプチャして、画像として個人フォルダに保存します。処理は1〜2分ほどで、ダイアログを閉じても続きます';
export const URL_CAPTURE_SCROLL_VIDEO_TOOLTIP = 'キャプチャが終わると、その画像でスクロール動画の編集画面に進みます。画像は個人フォルダにも保存されます';

/** 高さの予算で切ったときの注記 (保存はしている・11.2.6)。 */
export const URL_CAPTURE_TRUNCATED_NOTE = 'ページが長いため上から32000pxまでを取得しました';

export function urlCaptureDeviceErrorMessage(result: Pick<UrlCaptureDeviceResult, 'error_code' | 'error'> | null | undefined): string {
  const code = result?.error_code;
  if (code && code in URL_CAPTURE_DEVICE_ERROR_MESSAGES) {
    return URL_CAPTURE_DEVICE_ERROR_MESSAGES[code as UrlCaptureDeviceErrorCode];
  }
  return result?.error || URL_CAPTURE_DEVICE_ERROR_MESSAGES.CAPTURE_FAILED;
}

/** 投入 API のエラー → 利用者向け文言 (BE の error を優先し、無ければ error_code の既定文言)。 */
export function urlCaptureRequestErrorMessage(err: unknown): string {
  const data = (err as AxiosError<{ error?: string; error_code?: string }> | undefined)?.response?.data;
  if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  if (data?.error_code && URL_CAPTURE_REQUEST_ERROR_MESSAGES[data.error_code]) return URL_CAPTURE_REQUEST_ERROR_MESSAGES[data.error_code]!;
  if (err instanceof Error && err.message && !/status code/i.test(err.message)) return err.message;
  return 'キャプチャを開始できませんでした';
}

// ============================================================
// URL・素材メタ
// ============================================================

/**
 * 入力欄の URL を整える。スキームが無ければ https:// を補う。http/https 以外・ホストが無いものは null
 * (公開アドレスかどうかの判定は BE の validate_public_url が正本)。
 */
export function normalizeCaptureUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.hostname || !url.hostname.includes('.')) return null;
    return url.toString();
  } catch {
    return null;
  }
}

export function captureHostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

/** 素材の generation_metadata.url_capture (11.2.4)。 */
export interface UrlCaptureMeta {
  job_id?: string;
  url?: string;
  final_url?: string;
  device?: UrlCaptureDevice;
  viewport?: { w: number; h: number };
  dpr?: number;
  css_height?: number;
  truncated?: boolean;
  removed_overlays?: number;
  kept_fixed_header?: boolean;
  captured_at?: string;
}

export function readUrlCaptureMeta(metadata: Record<string, unknown> | null | undefined): UrlCaptureMeta | null {
  const raw = metadata?.url_capture;
  if (!raw || typeof raw !== 'object') return null;
  const meta = raw as UrlCaptureMeta;
  const device = meta.device === 'phone' || meta.device === 'pc' ? meta.device : undefined;
  return { ...meta, device };
}
